import React, { useMemo, useState } from "react";

import { fetchAttendances, updateAttendanceAmounts } from "@services/api";

import { useAppStore } from "@store/appStateStore";

import ParticipantInput from "../control/participant/select/participantInput";

import { sortParticipantsByName } from "../utils/sort";

const UpdateAttendanceAmountsForm: React.FC = () => {
  const {
    participant,
    participants,
    attendances,
    setAttendances,
    setParticipant,
  } = useAppStore();

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [participantId, setParticipantId] = useState<number>(participant ?? 0);
  const [newAmount, setNewAmount] = useState<number>(0);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"success" | "error">(
    "success",
  );

  // Количество посещений выбранного участника за выбранный месяц
  const countAttendances = useMemo(() => {
    if (participantId === 0) return 0;

    return attendances.filter((a) => {
      if (a.participant_id !== participantId) return false;

      // Извлекаем месяц из строки даты (формат: YYYY-MM-DD)
      const attendanceDate = new Date(a.date);
      const attendanceMonth = attendanceDate.getMonth() + 1; // getMonth() возвращает 0-11

      return attendanceMonth === month;
    }).length;
  }, [attendances, participantId, month]);

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();

    if (participantId === 0 || newAmount <= 0) {
      setMessage("Заполните форму");
      return;
    }

    setIsUpdating(true);
    setMessage("");

    try {
      await updateAttendanceAmounts(year, month, newAmount, participantId);
      setAttendances(await fetchAttendances());

      // Сброс формы
      setNewAmount(0);
    } catch (error) {
      console.error("Ошибка при обновлении стоимости посещений:", error);
      setMessage("Не удалось обновить стоимость посещений");
      setMessageType("error");
    } finally {
      setIsUpdating(false);
    }
  };

  // Генерируем список лет для выбора (от 2020 до текущего года + 5)
  const years = Array.from(
    { length: new Date().getFullYear() - 2020 + 6 },
    (_, i) => 2020 + i,
  );

  // Месяцы
  const months = [
    { value: 1, label: "Январь" },
    { value: 2, label: "Февраль" },
    { value: 3, label: "Март" },
    { value: 4, label: "Апрель" },
    { value: 5, label: "Май" },
    { value: 6, label: "Июнь" },
    { value: 7, label: "Июль" },
    { value: 8, label: "Август" },
    { value: 9, label: "Сентябрь" },
    { value: 10, label: "Октябрь" },
    { value: 11, label: "Ноябрь" },
    { value: 12, label: "Декабрь" },
  ];

  return (
    <form className="update-amounts-form" onSubmit={handleSubmit}>
      <div>
        <label htmlFor="participantSelect" className="control">
          Участник:
        </label>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <ParticipantInput
            id="participantSelect"
            value={participantId}
            onChange={(id: any) => {
              setParticipantId(id);
              setParticipant(id);
            }}
            required={true}
            options={sortParticipantsByName(participants)}
          />
          <button
            style={{ marginLeft: "1rem", width: 40 }}
            type="button"
            className="control"
            disabled={!participantId}
            onClick={() => {
              setParticipantId(0);
              setParticipant(null);
            }}
          >
            {"X"}
          </button>
        </div>
      </div>

      <div className="container-row">
        <div>
          <label htmlFor="yearSelect" className="control">
            Год:
          </label>
          <select
            id="yearSelect"
            className="control"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={isUpdating}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="yearSelect" className="control">
            Занятий за месяц:
          </label>
          {countAttendances}
        </div>

        <div>
          <label htmlFor="monthSelect" className="control">
            Месяц:
          </label>
          <select
            id="monthSelect"
            className="control"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            disabled={isUpdating}
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ paddingBottom: "1rem" }}>
        <label htmlFor="newAmountInput" className="control">
          Новая стоимость занятия:
        </label>
        <input
          id="newAmountInput"
          type="number"
          className="control"
          value={newAmount}
          onChange={(e) => setNewAmount(Number(e.target.value))}
          min="0"
          step="0.01"
          placeholder="Введите новую стоимость"
          disabled={isUpdating}
        />
      </div>

      <button
        type="submit"
        className="control"
        disabled={isUpdating || newAmount <= 0}
      >
        {isUpdating ? "Обновление..." : "Обновить стоимость"}
      </button>

      {message && (
        <div className={`result-message ${messageType}`}>{message}</div>
      )}
    </form>
  );
};

export default UpdateAttendanceAmountsForm;
